import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../context/CartContext';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, getTotal, clearCart, removeFromCart, updateQuantity } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    insurance: '',
    policyNumber: ''
  });

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      clearCart();
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }
  }, [searchParams, clearCart, navigate]);

  if (searchParams.get('success') === 'true') {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 pb-20 px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-4xl text-primary"></i>
            </div>
            <h1 className="font-serif text-4xl text-navy mb-3">Payment Successful!</h1>
            <p className="text-lg text-navy/60 mb-8">
              Thank you for your payment. Our team will contact you within 24 hours to schedule your appointments.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-7 py-3.5 bg-primary text-white rounded-full font-medium hover:bg-primary-dark transition-all cursor-pointer whitespace-nowrap"
            >
              Return to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (searchParams.get('canceled') === 'true') {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 pb-20 px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-6">
              <i className="ri-close-line text-4xl text-accent"></i>
            </div>
            <h1 className="font-serif text-4xl text-navy mb-3">Payment Canceled</h1>
            <p className="text-lg text-navy/60 mb-8">
              Your payment was canceled. Your cart items are still saved.
            </p>
            <button
              onClick={() => navigate('/checkout')}
              className="px-7 py-3.5 bg-primary text-white rounded-full font-medium hover:bg-primary-dark transition-all cursor-pointer whitespace-nowrap"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 pb-20 px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-sand rounded-full mx-auto mb-6">
              <i className="ri-shopping-bag-line text-4xl text-navy/30"></i>
            </div>
            <h1 className="font-serif text-4xl text-navy mb-3">Your Cart is Empty</h1>
            <p className="text-lg text-navy/60 mb-8">
              Add some services to get started with your dental care
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-7 py-3.5 bg-primary text-white rounded-full font-medium hover:bg-primary-dark transition-all cursor-pointer whitespace-nowrap"
            >
              Browse Services
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          customerInfo: formData
        }),
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      <div className="pt-28 pb-20 px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-serif text-4xl text-navy mb-3">Checkout</h1>
            <p className="text-navy/60">Complete your booking in just a few steps</p>
          </div>

          <div className="grid grid-cols-3 gap-10">
            {/* Left Side - Payment Form */}
            <div className="col-span-2">
              <form onSubmit={handleCheckout} className="space-y-6">
                {/* Contact Information */}
                <div className="bg-white rounded-2xl p-6 border border-sand/50">
                  <h2 className="font-serif text-xl text-navy mb-5">Contact Information</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Insurance Information */}
                <div className="bg-white rounded-2xl p-6 border border-sand/50">
                  <h2 className="font-serif text-xl text-navy mb-5">Insurance Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        Insurance Provider
                      </label>
                      <select 
                        name="insurance"
                        value={formData.insurance}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      >
                        <option value="">No Insurance</option>
                        <option value="aetna">Aetna</option>
                        <option value="cigna">Cigna</option>
                        <option value="delta">Delta Dental</option>
                        <option value="metlife">MetLife</option>
                        <option value="united">United Healthcare</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy/60 mb-1.5">
                        Policy Number (Optional)
                      </label>
                      <input
                        type="text"
                        name="policyNumber"
                        value={formData.policyNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-sand/30 border border-sand rounded-lg focus:outline-none focus:border-primary text-navy text-sm"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full py-3.5 bg-accent text-white rounded-full font-medium hover:bg-accent-dark transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-line"></i>
                      Proceed to Payment
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-navy/40">
                  <i className="ri-shield-check-line text-lg text-primary"></i>
                  <span className="text-sm">Secure payment powered by Stripe</span>
                </div>
              </form>
            </div>

            {/* Right Side - Order Summary */}
            <div className="col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl p-6 border border-sand/50">
                <h2 className="font-serif text-xl text-navy mb-5">Order Summary</h2>

                <div className="space-y-3 mb-5">
                  {items.map((item) => (
                    <div key={item.service.id} className="flex items-start justify-between pb-3 border-b border-sand">
                      <div className="flex-1">
                        <h3 className="font-medium text-navy text-sm mb-1">{item.service.name}</h3>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.service.id, item.quantity - 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-sand hover:bg-warm transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-xs text-navy"></i>
                          </button>
                          <span className="text-xs text-navy/60 min-w-[16px] text-center">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.service.id, item.quantity + 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-sand hover:bg-warm transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-xs text-navy"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.service.id)}
                            className="ml-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer group"
                          >
                            <i className="ri-delete-bin-line text-xs text-navy/30 group-hover:text-red-500"></i>
                          </button>
                        </div>
                      </div>
                      <div className="font-medium text-navy text-sm">
                        ${(item.service.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 mb-5">
                  <div className="flex items-center justify-between text-navy/60 text-sm">
                    <span>Subtotal</span>
                    <span className="font-medium">${getTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-navy/60 text-sm">
                    <span>Tax (8%)</span>
                    <span className="font-medium">${(getTotal() * 0.08).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-sand pt-2 flex items-center justify-between">
                    <span className="font-serif text-lg text-navy">Total</span>
                    <span className="font-serif text-xl text-primary">
                      ${(getTotal() * 1.08).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="bg-primary/5 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-primary mt-0.5"></i>
                    <div className="text-xs text-navy/60">
                      <strong className="block mb-0.5 text-navy">Note:</strong>
                      This payment reserves your services. Our team will contact you within 24 hours to schedule.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
